import { useEffect, useState } from "react";

import {
    ArrowButton, Checkbox,
    Select, Button, Modal, AutocompleteMultipleSelect,
    TableMetadata, TableStateWithFilters, useTable, ColumnSortEnum, Label
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { getCountyList, searchMunicipalities, searchTowns } from "../../api/Geography/GeographyApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import {
    searchSchoolCandidateInstitutionSummary,
    saveSchoolCandidateInstitutionStatus,
    getSchoolCandidateInstitutionSummaryListExport,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { ContentArea, FormCard, ListTable, SearchCard, ToolbarTop } from "../../components";
import { useAuth } from "../../hooks";
import { HierarchicalCodeBookPagedFilter, HierarchicalCodeBook, GrandParentCodeBookPagedFilter, GreatGrandParentCodeBookPagedFilter, InstitutionCodeBook, InstitutionCodeBookSearch } from "../../models";
import {
    SchoolCandidateInstitutionSummarySearch,
    SchoolCandidateSentStatus, SchoolCandidateInstitutionSummary,
    UpdateSchoolCandidateInstitutionStatus,
    SchoolCandidateInstitutionSummarySort
} from "../../models/SchoolCandidate";
import { ClaimType } from "../../models/User";
import { schoolCandidateInstitutionListUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";
import { getEnumToCodeBook } from "../../utils/msgUtils";

export function SchoolCandidateInstitutionSummaryList() {
    const { t } = useTranslation();
    const history = useHistory();
    const auth = useAuth();
    //STORAGE KEY
    const storageKey = 'SchoolSummarySearchTableState';
    //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
    const createInitialTableState = () => {
        return TableUtils.CreateInitialTableState<SchoolCandidateInstitutionSummarySearch>(
            storageKey,
            TableUtils.tableDefaultState(SchoolCandidateInstitutionSummarySort.Institution, ColumnSortEnum.ASC)
        )
    }
    const [initialTableState] = useState<TableStateWithFilters<SchoolCandidateInstitutionSummarySearch>>(() => createInitialTableState());
    //INIT SEARCH FILTER
    const createInitialSchoolCandidateInstitutionSummarySearch = (): SchoolCandidateInstitutionSummarySearch => {
        return {
            countyList: initialTableState.countyList
                ? initialTableState.countyList
                : defaultCountyIds,
            municipalityList: initialTableState.municipalityList,
            townList: initialTableState.townList,
            institutionId: initialTableState.institutionId,
            status: initialTableState.status,
            onlyWithUnsentSchoolCandidates: initialTableState.onlyWithUnsentSchoolCandidates
        }
    }
    const defaultCountyIds =
        auth.currentRole?.claimType === ClaimType.County &&
            (auth.currentRole?.locations?.length ?? 0) > 0
            ? auth.currentRole.locations
            : [] as number[]
    //TABLE DATA
    const defaultTableData = TableUtils.tableDefaultData<SchoolCandidateInstitutionSummary>()
    const [tableData, setTableData] = useState<PagedResult<SchoolCandidateInstitutionSummary>>(defaultTableData);
    //SET SEARCH FILTERS
    const [schoolCandidateInstitutionSummarySearch, setSchoolCandidateInstitutionSummarySearch] = useState<SchoolCandidateInstitutionSummarySearch>(() => createInitialSchoolCandidateInstitutionSummarySearch());
    const [tableSearch, setTableSearch] = useState<SchoolCandidateInstitutionSummarySearch>(() => createInitialSchoolCandidateInstitutionSummarySearch());
    const [filterChanged, setFilterChanged] = useState<boolean>(false);
    const [clearFilter, setClearFilter] = useState<boolean>(true);
    //TABLE METADATA
    const tableMetadata: TableMetadata<SchoolCandidateInstitutionSummary> = {
        rowKey: "id",
        columns: [
            {
                columnKey: "select",
                header: "",
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (
                        <div>
                            <Checkbox
                                label={""}
                                name={nameof<typeof item>("id")}
                                value={item.id}
                                checked={item.isSelected}
                                onChange={handleInstitutionCheckedChange}
                                disabled={!getSendStatusEnabled(item)}
                            />
                        </div>)
                },
            },
            {
                columnKey: "municipalityList",
                header: t("common.schoolCandidateModel.municipalityList"),
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (
                        <div>
                            {item.municipality?.name}
                        </div>)
                },
                sortBy: SchoolCandidateInstitutionSummarySort.County,
            },
            {
                columnKey: "townList",
                header: t("common.schoolCandidateModel.townList"),
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (
                        <div>
                            {item.town?.name}
                        </div>)
                },
                sortBy: SchoolCandidateInstitutionSummarySort.Town,
            },
            {
                columnKey: "institutionList",
                header: t("common.schoolCandidateModel.institutionList") + "/" + t("common.schoolCandidateModel.firstName"),
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (
                        <div>
                            {item.name}
                        </div>)
                },
                sortBy: SchoolCandidateInstitutionSummarySort.Institution,
            },
            {
                columnKey: "schoolCandidateCount",
                header: t("common.schoolCandidateModel.totalCount"),
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (<div>
                        {item.schoolCandidateCount}
                    </div>)
                },
                sortBy: SchoolCandidateInstitutionSummarySort.SchoolCandidateCount,
            },
            {
                columnKey: "schoolCandiateSentCount",
                header: t("common.schoolCandidateModel.sentCount"),
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (<div>
                        {item.schoolCandiateSentCount}
                    </div>)
                },
                sortBy: SchoolCandidateInstitutionSummarySort.SchoolCandiateSentCount,
            },
            {
                columnKey: "status",
                header: t("common.schoolCandidateModel.status"),
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (
                        <>
                            <Label color={FormatUtils.getSchoolCandidateSentStatusColor(item.status)} className="text-center" outline={false} fullWidth={false}>
                                {statuses.find(val => val.id === item.status)?.name}
                            </Label>
                        </>
                    )
                },
                sortBy: SchoolCandidateInstitutionSummarySort.Status,
            },
            {
                columnKey: "details",
                header: "",
                accessor: (item: SchoolCandidateInstitutionSummary) => {
                    return (<div className={"text-right"}>
                        <ArrowButton
                            ariaLabel={t("common.details")}
                            onClick={() =>
                                handleDetailsClicked(item.id)
                            } />
                    </div>)
                }
            }
        ],
        rowToolTip: () => undefined
    };
    //SET TABLE
    const [tableProps, tableState, setFilters] = useTable<SchoolCandidateInstitutionSummarySearch>(
        {
            metadata: tableMetadata,
            data: tableData!.results,
            pagination: true,
            paginationPage: initialTableState.page,
            paginationPageSize: initialTableState.pageSize as any,
            totalItems: tableData!.rowCount,
            defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
        }
    );
    //FILTER DATA 
    const [counties, setCounties] = useState<CodeBook[]>();
    const [filteredMunicipalities, setFilteredMunicipalities] = useState<HierarchicalCodeBook[]>([]);
    const [filteredTowns, setFilteredTowns] = useState<HierarchicalCodeBook[]>([]);
    const [filteredInstitutions, setFilteredInstitutions] = useState<InstitutionCodeBook[]>([]);
    const [statuses] = useState<CodeBook[]>(getEnumToCodeBook(SchoolCandidateSentStatus, "SchoolCandidateSentStatus"));
    //EDIT DATA 
    const createDefaultUpdate = (): UpdateSchoolCandidateInstitutionStatus => {
        return {
            institutions: [],
        };
    };
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [updateSchoolCandidateInstitutionStatus, setUpdateSchoolCandidateInstitutionStatus] = useState<UpdateSchoolCandidateInstitutionStatus>(createDefaultUpdate());
    const [sendStatusEnabled, setSendStatusEnabled] = useState<boolean>(false);
    const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
    const getSendStatusEnabled = (item: SchoolCandidateInstitutionSummary): boolean => {
        return item.schoolCandidateCount > 0 && item.schoolCandiateSentCount !== item.schoolCandidateCount;
    };


    useEffect(() => {
        (async () => {
            var counties = await getCountyList();
            if (auth.currentRole?.claimType === ClaimType.County) {
                counties = counties.filter(v => auth.currentRole?.locations?.find(l => v.id === l) !== undefined);
            }
            setCounties(counties);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (clearFilter) {
            setClearFilter(false);
            setFilterChanged(false);
        }
        else
            setFilterChanged(true);
        TableUtils.RemoveSessionStorageItem(storageKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolCandidateInstitutionSummarySearch]);

    useEffect(() => {
        (async () => {
            var request = TableUtils.GetSearchRequest<SchoolCandidateInstitutionSummarySearch>(tableSearch, tableState);
            var itemList = await searchSchoolCandidateInstitutionSummary(request);
            if (itemList !== null && itemList.results.length > 0) {
                setTableData(itemList);
            }
            else {
                setTableData(defaultTableData);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    const handleInstitutionFilterChange = async (filter: string) => {
        const search: InstitutionCodeBookSearch = {
            search: filter,
            onlyActive: false,
            countyIds: schoolCandidateInstitutionSummarySearch.countyList,
            municipalityIds: schoolCandidateInstitutionSummarySearch.municipalityList?.map(v => v.id),
            townIds: schoolCandidateInstitutionSummarySearch.townList?.map(v => v.id),
            includeOutsideEnrollmentArea: true,
            includeHandicapped: undefined, // both
            includePrivate: undefined, // both
            includeSatellite: undefined, // both
        };
        var result = await searchInstitutionNew(search);
        setFilteredInstitutions(result);
    };
    const handleCountyChange = (e: any) => {
        const countyList = e.target.value as number[];
        const newSearch: SchoolCandidateInstitutionSummarySearch = {
            ...schoolCandidateInstitutionSummarySearch,
            countyList: countyList,
            municipalityList: [],
            townList: [],
        };
        setFilteredMunicipalities([]);
        setFilteredTowns([]);
        setSchoolCandidateInstitutionSummarySearch(newSearch);
    };
    const handleMunicipalityFilterChange = async (filter: string) => {
        const search: HierarchicalCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: schoolCandidateInstitutionSummarySearch.municipalityList?.map(val => val.id),
            parentId: schoolCandidateInstitutionSummarySearch.countyList,
            search: filter,
        };
        var result = await searchMunicipalities(search);
        setFilteredMunicipalities(result.results);
    };
    const handleTownFilterChange = async (filter: string) => {
        const search: GrandParentCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: schoolCandidateInstitutionSummarySearch.townList?.map(val => val.id),
            parentId: schoolCandidateInstitutionSummarySearch.municipalityList?.map(val => val.id),
            grandParentId: schoolCandidateInstitutionSummarySearch.countyList,
            search: filter,
        };
        var result = await searchTowns(search);
        setFilteredTowns(result.results);
    };
    const handleMunicipalityChange = (e: any) => {
        const municipalityList = e.target.value as HierarchicalCodeBook[];
        const newSearch: SchoolCandidateInstitutionSummarySearch = {
            ...schoolCandidateInstitutionSummarySearch,
            municipalityList: municipalityList,
            townList: [],
        };
        setFilteredTowns([]);
        setSchoolCandidateInstitutionSummarySearch(newSearch);
    };
    const handleTownChange = (e: any) => {
        const townList = e.target.value as HierarchicalCodeBook[];
        const newSearch: SchoolCandidateInstitutionSummarySearch = {
            ...schoolCandidateInstitutionSummarySearch,
            townList: townList,
        };
        setSchoolCandidateInstitutionSummarySearch(newSearch);
    };
    const handleInstitutionChange = (e: any) => {
        const institutionList = e.target.value as CodeBook[];
        const newSearch: SchoolCandidateInstitutionSummarySearch = {
            ...schoolCandidateInstitutionSummarySearch,
            institutionId: institutionList && institutionList.length > 0 ? institutionList[0].id : undefined,
        };
        setSchoolCandidateInstitutionSummarySearch(newSearch);
    };
    const handleStatusChange = (e: any) => {
        const status = Number.parseInt(e.target.value);
        const newSearch: SchoolCandidateInstitutionSummarySearch = {
            ...schoolCandidateInstitutionSummarySearch,
            status: !isNaN(status) && status > 0 ? status : undefined,
        };
        setSchoolCandidateInstitutionSummarySearch(newSearch);
    };
    const handleOnlyWithUnsentSchoolCandidatesChange = (val: boolean) => {
        const newSearch: SchoolCandidateInstitutionSummarySearch = {
            ...schoolCandidateInstitutionSummarySearch,
            onlyWithUnsentSchoolCandidates: val,
        };
        setSchoolCandidateInstitutionSummarySearch(newSearch);
    };

    const handleDetailsClicked = (id?: number) => {
        if (id) {
            TableUtils.SetSessionStorageItem<SchoolCandidateInstitutionSummarySearch>(storageKey, tableSearch, tableState);
            history.push(
                schoolCandidateInstitutionListUrl(
                    id!
                )
            );
        }
    };

    const handleInstitutionCheckedChange = (e: any) => {
        const institutionId = Number.parseInt(e.target.value);
        const isChecked = e.target.checked as boolean;
        if (institutionId) {
            var institution = tableData.results.find(val => val.id === institutionId);
            var checkedInstitutionList = updateSchoolCandidateInstitutionStatus.institutions;
            if (isChecked && institution) {
                checkedInstitutionList.push(institutionId);
                institution!.isSelected = true;
            } else {
                checkedInstitutionList = checkedInstitutionList.filter(val => val !== institutionId);
                setSelectAll(false);
                institution!.isSelected = false;
            }
            const newUpdate: UpdateSchoolCandidateInstitutionStatus = {
                ...updateSchoolCandidateInstitutionStatus,
                institutions: checkedInstitutionList,
            };
            setUpdateSchoolCandidateInstitutionStatus(newUpdate);
            setSendButtons(newUpdate);
        }
    };
    const setSendButtons = (u: UpdateSchoolCandidateInstitutionStatus) => {
        setSendStatusEnabled(u.institutions.length > 0);
    };
    const handleSelectAllCheckedChange = (e: any) => {
        const isChecked = !selectAll;
        setSelectAll(isChecked);
        const newResults = tableData.results.map(val => { if (getSendStatusEnabled(val)) val.isSelected = isChecked; return val; });
        const newTableData: PagedResult<SchoolCandidateInstitutionSummary> = {
            ...tableData,
            results: newResults,
        };
        const newUpdate: UpdateSchoolCandidateInstitutionStatus = {
            ...updateSchoolCandidateInstitutionStatus,
            institutions: newResults.filter(val => val.isSelected).map(val => val.id),
        }
        setTableData(newTableData);
        setUpdateSchoolCandidateInstitutionStatus(newUpdate);
        setSendButtons(newUpdate);
    };

    const handleSendStatusClicked = async () => {
        var result = await saveSchoolCandidateInstitutionStatus(updateSchoolCandidateInstitutionStatus);
        if (result > 0) {
            setShowSaveModal(true);
            setTableSearch(schoolCandidateInstitutionSummarySearch)
            setFilterChanged(false);
            search();
        }
    };

    const handleSearchClicked = (e: any) => {
        setTableSearch(schoolCandidateInstitutionSummarySearch)
        setFilterChanged(false);
        search();
    };
    const handleClearSearchClicked = () => {
        setClearFilter(true);
        setSchoolCandidateInstitutionSummarySearch({ countyList: defaultCountyIds, onlyWithUnsentSchoolCandidates: false });
        setTableSearch({ countyList: defaultCountyIds, onlyWithUnsentSchoolCandidates: false })
        setSendButtons(createDefaultUpdate());
        setSelectAll(false);
        search();
    };
    const search = () => {
        setFilters({ ...tableSearch });
    };
    const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
        await getSchoolCandidateInstitutionSummaryListExport(schoolCandidateInstitutionSummarySearch, exportType);
    };


    return (
        <ContentArea>
            <ToolbarTop pageTitle={t("common.schoolCandidateModel.initialListPageName")}
            >
            </ToolbarTop>
            <SearchCard
                onClickSearch={handleSearchClicked}
                onClickClearSearch={handleClearSearchClicked}
                isFilterChanged={filterChanged}
                hideDivider={true}
            >
                <Row >
                    <Col md={6}>
                        <Select
                            multiple
                            label={t("common.schoolCandidateModel.countyList")}
                            name={nameof<typeof schoolCandidateInstitutionSummarySearch>("countyList")}
                            value={schoolCandidateInstitutionSummarySearch.countyList}
                            onChange={handleCountyChange}
                            selectData={codeBookToSelectData(counties)}
                        />
                    </Col>
                    <Col md={6}>
                        <AutocompleteMultipleSelect<CodeBook>
                            label={t("common.schoolCandidateModel.institutionList")}
                            name={nameof<typeof schoolCandidateInstitutionSummarySearch>("institutionId")}
                            value={filteredInstitutions.filter(val => val.id === schoolCandidateInstitutionSummarySearch.institutionId)}
                            valueProp="id"
                            textProp="name"
                            onChange={handleInstitutionChange}
                            selectOptions={filteredInstitutions}
                            onFilterChange={handleInstitutionFilterChange}
                            maximumSelectedItems={10}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={6}>
                        <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.municipalityList")}
                            name={nameof<typeof schoolCandidateInstitutionSummarySearch>("municipalityList")}
                            value={schoolCandidateInstitutionSummarySearch.municipalityList ?? []}
                            valueProp="id"
                            textProp="name"
                            onChange={handleMunicipalityChange}
                            selectOptions={filteredMunicipalities}
                            onFilterChange={handleMunicipalityFilterChange}
                            maximumSelectedItems={10}
                        />
                    </Col>
                    <Col md={6}>
                        <Select
                            label={t("common.schoolCandidateModel.status")}
                            name={nameof<typeof schoolCandidateInstitutionSummarySearch>("status")}
                            value={schoolCandidateInstitutionSummarySearch.status}
                            onChange={handleStatusChange}
                            selectData={codeBookToSelectData(statuses)}
                            emptyOption={{ value: undefined, text: t("common.all") }}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={6}>
                        <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.townList")}
                            name={nameof<typeof schoolCandidateInstitutionSummarySearch>("townList")}
                            value={schoolCandidateInstitutionSummarySearch.townList ?? []}
                            valueProp="id"
                            textProp="name"
                            onChange={handleTownChange}
                            selectOptions={filteredTowns}
                            onFilterChange={handleTownFilterChange}
                            maximumSelectedItems={10}
                        />
                    </Col>
                    <Col md={6}>
                        <Checkbox
                            label={t("common.schoolCandidateModel.onlyWithUnsentSchoolCandidates")}
                            name={"onlyWithUnsentSchoolCandidates"}
                            checked={schoolCandidateInstitutionSummarySearch.onlyWithUnsentSchoolCandidates}
                            onChange={(e) => handleOnlyWithUnsentSchoolCandidatesChange(e.target.checked)}
                        />
                    </Col>
                </Row>
            </SearchCard>
            <FormCard>
                <Row className="m-1 p-2 pt-4 bg-light border">
                    <Col md={2} className="pt-1">
                        <Checkbox
                            label={t("common.selectAll")}
                            name={"selectAll"}
                            checked={selectAll}
                            onChange={handleSelectAllCheckedChange}
                        />
                    </Col>
                    <Col md={10}>
                        <Button onClick={handleSendStatusClicked}
                            size="sm"
                            disabled={!sendStatusEnabled || filterChanged}
                        >
                            {t("common.schoolCandidateModel.sendList")}
                        </Button>
                    </Col>
                </Row>

            </FormCard>
            <Modal
                type={"message"}
                confirmText={t("common.close")}
                open={showSaveModal}
                onConfirm={() => setShowSaveModal(false)}
                onClose={() => setShowSaveModal(false)}
            >
                {t("common.saveSuccess")}
            </Modal>
            <ListTable
                tableProps={tableProps}
                onClickDownload={(exportType: ExportTypeEnum) => handleDownloadClicked(exportType)}
                showDownload
            />
        </ContentArea>
    );
}